import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Stethoscope, GraduationCap, Scale, Factory, Swords } from "lucide-react";

const schema = z.object({
  fullName: z.string().min(2, "Please enter your full name"),
  email: z.string().email("Please enter a valid email"),
  company: z.string().min(2, "Please enter your organization"),
  sector: z.enum(["Healthcare", "Education", "Legal", "Defence", "Manufacturing", "Other"], {
    required_error: "Please select a sector",
  }),
  role: z.string().optional(),
  message: z.string().min(20, "Tell us a bit more (20+ characters)"),
});

type FormValues = z.infer<typeof schema>;

const MaplePTCollaborationSection: React.FC = () => {
  const form = useForm<FormValues>({ resolver: zodResolver(schema) });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);

    const [first_name, ...rest] = values.fullName.trim().split(" ");
    const last_name = rest.join(" ") || "";

    const composedMessage =
      `Sector: ${values.sector}\n` +
      (values.role ? `Role: ${values.role}\n` : "") +
      `Message:\n${values.message}`;

    const payload = {
      first_name,
      last_name,
      company_email: values.email,
      company: values.company,
      job_title: values.role ?? "Collaborator",
      company_size: null as string | null,
      message: composedMessage,
    };

    // Save to Supabase
    const { error } = await supabase.from("enterprise_requests").insert(payload);
    if (error) {
      setSubmitting(false);
      toast({
        title: "Submission failed",
        description: "Please try again or email hello@mapleos.ai.",

        variant: "destructive",
      });
      return;
    }

    // Send email via dedicated Edge Function
    const { data: emailData, error: emailError } = await supabase.functions.invoke("send-maplept-collaboration", {
      body: {
        firstName: first_name,
        lastName: last_name,
        companyEmail: values.email,
        company: values.company,
        jobTitle: values.role ?? "Collaborator",
        companySize: null,
        message: composedMessage,
        recipients: ["vincem@mapleos.ai", "erich@mapleos.ai"],

        source: "maplept-collaboration",
      },
    });

    if (emailError || (emailData && emailData.success === false)) {
      setSubmitting(false);
      toast({
        title: "Email failed",
        description: "We saved your request, but email notification failed. We'll follow up shortly.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Thank you!",
      description: "We'll reach out within 1–2 business days.",
    });
    form.reset();
    setSubmitting(false);
  };

  return (
    <section className="container mx-auto px-4 py-12 md:py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        <div className="max-w-3xl md:max-w-none">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">We're looking for Collaborators</h2>
          <p className="mt-3 text-muted-foreground">
            We're building small language models (SLMs) that are fast, private, and highly focused.
            SLMs can run on modest hardware, align tightly to domain knowledge, and reduce operational costs—
            ideal for Healthcare, Education, Legal, Defence, and Manufacturing.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-muted-foreground">
            <div className="flex items-start gap-3">
              <ShieldCheck className="text-primary" />
              <div>
                <div className="font-semibold">Privacy-first</div>
                <div className="text-sm">On-prem and edge-friendly deployments keep data sovereign.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Factory className="text-primary" />
              <div>
                <div className="font-semibold">Operationally lean</div>
                <div className="text-sm">Lower compute costs and simpler scaling for specialized tasks.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GraduationCap className="text-primary" />
              <div>
                <div className="font-semibold">Domain-tuned</div>
                <div className="text-sm">Fine-tuned to your curricula, procedures, or SOPs.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Swords className="text-primary" />
              <div>
                <div className="font-semibold">Resilient</div>
                <div className="text-sm">Small footprint with robust instruction-following.</div>
              </div>
            </div>
          </div>
        </div>

        <Card className="mt-8 md:mt-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Stethoscope className="text-primary" />
              <Scale className="text-primary" />
              <Factory className="text-primary" />
              <h3 className="text-xl md:text-2xl font-semibold">Partner on sector-specific SLM experts</h3>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane@organization.ca" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization</FormLabel>
                      <FormControl>
                        <Input placeholder="Canadian Health Network" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sector"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sector</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select sector" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Healthcare">Healthcare</SelectItem>
                            <SelectItem value="Education">Education</SelectItem>
                            <SelectItem value="Legal">Legal</SelectItem>
                            <SelectItem value="Defence">Defence</SelectItem>
                            <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Your role (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Clinical Lead, Director of Operations" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>What do you want to build?</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Briefly describe your goals, constraints, and timeline…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="md:col-span-2 flex justify-end">
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Submitting…" : "Submit interest"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default MaplePTCollaborationSection;